import { useEffect, useState } from "react";

interface UnifiedLoadingScreenProps {
  progress: number;
  stage: string;
  bookTitle?: string;
  bookCoverUrl?: string;
  visible: boolean;
}

export function UnifiedLoadingScreen({
  progress,
  stage,
  bookTitle,
  bookCoverUrl,
  visible,
}: UnifiedLoadingScreenProps) {
  const [fadeOut, setFadeOut] = useState(false);

  // Trigger fade-out when loading complete
  useEffect(() => {
    if (visible) {
      setFadeOut(false);
      return;
    }

    if (!fadeOut) {
      // Small delay before fade to ensure content is rendered
      const timeoutId = globalThis.setTimeout(() => setFadeOut(true), 100);
      return () => globalThis.clearTimeout(timeoutId);
    }
  }, [visible, fadeOut]);

  // Remove from DOM after fade completes
  if (!visible && fadeOut) {
    return null;
  }

  return (
    <div
      className={`fixed inset-0 z-50 bg-[var(--color-bg-surface)] flex items-center justify-center transition-opacity duration-500 ${
        fadeOut ? "opacity-0" : "opacity-100"
      }`}
    >
      <div className="flex flex-col items-center gap-6 max-w-md px-6">
        {/* Book cover */}
        {bookCoverUrl && (
          <div className="relative">
            <img
              src={bookCoverUrl}
              alt={bookTitle}
              className="w-32 h-48 object-cover rounded-lg shadow-2xl"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer" />
          </div>
        )}

        {/* Circular progress indicator */}
        <div className="relative w-32 h-32">
          {/* Background circle */}
          <svg className="w-full h-full transform -rotate-90">
            <circle
              cx="64"
              cy="64"
              r="56"
              stroke="currentColor"
              strokeWidth="8"
              fill="none"
              className="text-[var(--color-border-subtle)]"
            />
            {/* Progress circle */}
            <circle
              cx="64"
              cy="64"
              r="56"
              stroke="currentColor"
              strokeWidth="8"
              fill="none"
              strokeDasharray={`${2 * Math.PI * 56}`}
              strokeDashoffset={`${2 * Math.PI * 56 * (1 - progress / 100)}`}
              className="text-orange-500 transition-all duration-300"
              strokeLinecap="round"
            />
          </svg>
          {/* Progress percentage */}
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-2xl font-semibold">{progress}%</span>
          </div>
        </div>

        {/* Stage text */}
        <div className="text-center space-y-2">
          <p className="text-lg font-medium text-[var(--color-text-primary)]">{stage}</p>
          {bookTitle && <p className="text-sm text-[var(--color-text-muted)]">{bookTitle}</p>}
        </div>
      </div>
    </div>
  );
}
